// --- API Hooks (Products) ---
import {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useGetProductBySlugQuery,
  useGetProductsCollectionDataQuery,
  useGetProductVariationsQuery,
  useGetProductsByIdsQuery,
} from "../templates/ecommerce/product/api/productApi";

// --- API Hooks (Taxonomies) ---
import {
  useGetCategoriesQuery,
  useGetVehiclesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
  useGetAttributesQuery,
} from "../templates/ecommerce/product/api/productTaxonomyApi";

// --- Custom Hooks ---
import { useVariationSelection } from "../templates/ecommerce/product/hooks/useVariationSelection";
import { useRelatedProducts } from "../templates/ecommerce/product/hooks/useRelatedProducts";
import { useSyncProductsArchiveStateToUrl } from "../templates/ecommerce/product/hooks/useSyncProductsArchiveStateToUrl";
import { useSyncProductSingleAttributesToUrl } from "../templates/ecommerce/product/hooks/useSyncProductSingleAttributesToUrl";

// --- Reusable UI Components ---
import ProductCard from "../templates/ecommerce/product/components/common/ProductCard/ProductCard";
import ProductCardSkeleton from "../templates/ecommerce/product/components/common/ProductCard/ProductCardSkeleton";
import ProductPanelItem from "../templates/ecommerce/product/components/common/ProductPanelItem/ProductPanelItem";
import ProductPanelItemSkeleton from "../templates/ecommerce/product/components/common/ProductPanelItem/ProductPanelItemSkeleton";
import ProductPriceDisplay from "../templates/ecommerce/product/components/common/ProductPriceDisplay";
import DeliveryEstimateDisplay from "../templates/ecommerce/product/components/common/DeliveryEstimateDisplay";

// --- Redux Actions ---
import {
  openProductModal,
  closeProductModal,
} from "../templates/ecommerce/product/components/common/ProductModal/productModalSlice";
import {
  toggleCategory,
  replaceVehicles,
  toggleTag,
  toggleBrand,
  setAttributeFilter,
  setPriceRange,
  setSearch,
  setSku,
  setSorting,
  setOnSale,
  setInStock,
  clearCategories,
  replaceCategories,
  setPage,
  setPerPage,
  setActiveView,
  resetFilters,
} from "../templates/ecommerce/product/productSlice";

// --- Redux Selectors ---
import {
  selectCategoryTree,
  selectAreFiltersActive,
  selectCurrencySymbol,
} from "../templates/ecommerce/product/productSelectors";

// --- Pure Data Helpers ---
import {
  findCategoryInTree,
  getDescendantIds,
} from "../templates/ecommerce/product/helpers/categoryTreeHelpers";
import {
  findVehiclePath,
  getVehicleModelOptions,
} from "../templates/ecommerce/product/helpers/vehicleTreeHelpers";

export const product = {
  // 1. API Data Fetching
  api: {
    useGetProductsQuery,
    useLazyGetProductsQuery,
    useGetProductByIdQuery,
    useGetProductBySlugQuery,
    useGetProductsCollectionDataQuery,
    useGetProductVariationsQuery,
    useGetProductsByIdsQuery,
    taxonomies: {
      useGetCategoriesQuery,
      useGetVehiclesQuery,
      useGetTagsQuery,
      useGetBrandsQuery,
      useGetAttributesQuery,
    },
  },

  // 2. Custom Business Logic Hooks
  hooks: {
    useVariationSelection,
    useRelatedProducts,
    useSyncProductsArchiveStateToUrl,
    useSyncProductSingleAttributesToUrl,
  },

  // 3. UI Components
  components: {
    ProductCard,
    ProductCardSkeleton,
    ProductPanelItem,
    ProductPanelItemSkeleton,
    ProductPriceDisplay,
    DeliveryEstimateDisplay,
  },

  // 4. Redux Actions (Control state from the theme)
  actions: {
    modal: {
      openProductModal,
      closeProductModal,
    },
    filters: {
      toggleCategory,
      replaceVehicles,
      toggleTag,
      toggleBrand,
      setAttributeFilter,
      setPriceRange,
      setSearch,
      setSku,
      setSorting,
      setOnSale,
      setInStock,
      clearCategories,
      replaceCategories,
      resetFilters,
    },
    ui: {
      setPage,
      setPerPage,
      setActiveView,
    },
  },

  // 5. Redux Selectors (Read state from the theme)
  selectors: {
    selectCategoryTree,
    selectAreFiltersActive,
    selectCurrencySymbol,
  },

  // 6. Data Helpers (For UI manipulation)
  helpers: {
    findVehiclePath,
    getVehicleModelOptions,
    findCategoryInTree,
    getDescendantIds,
  },
};
